import React, { useState, useEffect } from 'react';
import { examData } from '../../data/examData';
import { getKoreanExamName } from '../../utils/examNameMapping';
import { getKoreanCertificationName, getCertificationCategory, getCertificationLevel } from '../../utils/koreanNameMapper';

const IntegrationTest = () => {
  const [testResults, setTestResults] = useState([]);
  const [performanceStats, setPerformanceStats] = useState(null);

  useEffect(() => {
    runIntegrationTest();
  }, []);

  const runIntegrationTest = () => {
    console.log('=== examData.js와 한글 매핑 시스템 연동 테스트 시작 ===');
    
    const examKeys = Object.keys(examData);
    const results = [];
    
    // Performance test
    const start = performance.now();
    
    // 기존 시스템과 새 시스템 비교
    examKeys.forEach((key, index) => {
      const originalTitle = examData[key].title;
      const legacyMapping = getKoreanExamName(key);
      const newMapping = getKoreanCertificationName(key);
      const category = getCertificationCategory(key);
      const level = getCertificationLevel(key);
      
      results.push({
        index: index + 1,
        key,
        originalTitle,
        legacyMapping,
        newMapping,
        category,
        level,
        isMatch: legacyMapping === newMapping
      });
    });
    
    // Performance calculation
    const end = performance.now();
    const totalTime = end - start;
    
    setTestResults(results);
    setPerformanceStats({
      totalTime: totalTime.toFixed(2),
      averageTime: (totalTime / examKeys.length).toFixed(4),
      totalMappings: examKeys.length
    });
    
    console.log('✅ 연동 테스트 완료!');
  };

  const matchCount = testResults.filter(r => r.isMatch).length;
  const totalCount = testResults.length;
  const matchPercentage = totalCount > 0 ? ((matchCount / totalCount) * 100).toFixed(1) : 0;

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">
            examData.js ↔ 한글 매핑 시스템 연동 테스트
          </h1>
          
          {/* 통계 요약 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-blue-50 p-6 rounded-xl">
              <div className="text-2xl font-bold text-blue-700">{totalCount}</div>
              <div className="text-sm text-blue-600">총 자격증 수</div>
            </div>
            <div className="bg-green-50 p-6 rounded-xl">
              <div className="text-2xl font-bold text-green-700">{matchCount}</div>
              <div className="text-sm text-green-600">일치하는 매핑</div>
            </div>
            <div className="bg-purple-50 p-6 rounded-xl">
              <div className="text-2xl font-bold text-purple-700">{matchPercentage}%</div>
              <div className="text-sm text-purple-600">매핑 정확도</div>
            </div>
            <div className="bg-orange-50 p-6 rounded-xl">
              <div className="text-2xl font-bold text-orange-700">
                {performanceStats?.averageTime || '0'}ms
              </div>
              <div className="text-sm text-orange-600">평균 매핑 시간</div>
            </div>
          </div>
          
          {/* 성능 정보 */}
          {performanceStats && (
            <div className="bg-gray-50 p-4 rounded-lg mb-8">
              <h3 className="text-lg font-semibold mb-2">성능 테스트 결과</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>총 처리 시간: {performanceStats.totalTime}ms</div>
                <div>처리한 매핑 수: {performanceStats.totalMappings}개</div>
                <div>평균 매핑 시간: {performanceStats.averageTime}ms</div>
              </div>
            </div>
          )}
          
          {/* 테스트 결과 테이블 */}
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    자격증 키
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    examData 원본
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    새 매핑 결과
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    분야/레벨
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    일치
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {testResults.map((result) => (
                  <tr key={result.key} className={result.isMatch ? 'bg-green-50' : 'bg-red-50'}>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {result.index}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-gray-600">
                      {result.key}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {result.originalTitle}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {result.newMapping}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <div className="flex flex-col space-y-1">
                        <span className="inline-flex px-2 py-1 text-xs rounded bg-blue-100 text-blue-700">
                          {result.category}
                        </span>
                        {result.level && (
                          <span className="inline-flex px-2 py-1 text-xs rounded bg-purple-100 text-purple-700">
                            {result.level}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      {result.isMatch ? (
                        <span className="text-green-600 font-semibold">✅</span>
                      ) : (
                        <span className="text-red-600 font-semibold">❌</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationTest;